/**
 * Nhật ký hoạt động — các lần chạy (đã ghép các đoạn trước/sau khối Chờ theo
 * runId) với bộ lọc kết quả · thời lượng · khoảng ngày · chạy thật/thử. Bấm một
 * dòng để xem vết từng bước. Dùng trong trình dựng (một luồng) và ở danh sách (mọi luồng).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocale } from '@/lib/i18n'
import { Clock, History, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { useRunLogs } from '@/hooks/use-automation'
import type { KindKhoi } from '@/lib/automation/flow-adapter'
import {
  KET_QUA_LABEL, THOI_LUONG_LABEL, gopNhatKy, moTaThoiLuong, trongThoiLuong,
  type KetQuaChay, type LanChay, type ThoiLuong,
} from '@/lib/automation/run-result'
import { ChiTietLanChay } from './chi-tiet-lan-chay'

const fmt = (s?: string) => (s ? new Date(s).toLocaleString(currentLocale(), { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—')
const ngayISO = (d: Date) => d.toLocaleDateString('sv-SE') // yyyy-mm-dd theo giờ máy
const TAT_CA_KQ = Object.keys(KET_QUA_LABEL) as KetQuaChay[]
type Loai = 'all' | 'that' | 'thu'

/** yyyy-mm-dd theo giờ máy → thời điểm ISO đầu ngày (cộng `themNgay` ngày). */
const dauNgay = (ymd: string, themNgay = 0) => {
  if (!ymd) return undefined
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d + themNgay).toISOString()
}

export function NhatKyHoatDong({ ruleId, hienTenLuong = true, kinds }: { ruleId?: string; hienTenLuong?: boolean; kinds?: Map<string, KindKhoi> }) {
  const { t } = useTranslation('autoConfig')
  const [ketQua, setKetQua] = useState<KetQuaChay[]>(TAT_CA_KQ)
  const [thoiLuong, setThoiLuong] = useState<ThoiLuong>('all')
  const [loai, setLoai] = useState<Loai>('all')
  const [tu, setTu] = useState(() => ngayISO(new Date(Date.now() - 6 * 86_400_000)))
  const [den, setDen] = useState(() => ngayISO(new Date()))
  const [mo, setMo] = useState<LanChay | null>(null)
  // Khoảng ngày và thật/thử lọc ở server; kết quả + thời lượng (cần ghép đoạn) lọc ở đây.
  const { data, isLoading, isError, refetch, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } = useRunLogs(ruleId, {
    from: dauNgay(tu), to: dauNgay(den, 1), ...(loai !== 'all' ? { loai } : {}),
  })
  const rows = useMemo(() => data?.pages.flatMap((p) => p.logs) ?? [], [data])

  const runs = useMemo(() => gopNhatKy(rows, kinds), [rows, kinds])
  const hien = useMemo(() => runs.filter((r) => {
    if (!trongThoiLuong(r.thoiLuongMs, thoiLuong)) return false
    return ketQua.includes(r.ketQua)
  }), [runs, thoiLuong, ketQua])
  const dangLoc = ketQua.length !== TAT_CA_KQ.length || thoiLuong !== 'all' || loai !== 'all'

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 font-normal">
              {ketQua.length === TAT_CA_KQ.length ? t('log.allResults') : ketQua.length === 0 ? t('log.noResultSelected') : (
                <><span className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{KET_QUA_LABEL[ketQua[0]].ten}</span>{ketQua.length > 1 && <span className="text-[11px] text-muted-foreground">+{ketQua.length - 1}</span>}</>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {TAT_CA_KQ.map((k) => (
              <DropdownMenuCheckboxItem key={k} checked={ketQua.includes(k)} onSelect={(e) => e.preventDefault()}
                onCheckedChange={(v) => setKetQua((ds) => (v ? [...ds, k] : ds.filter((x) => x !== k)))}>
                {KET_QUA_LABEL[k].ten}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Select value={loai} onValueChange={(v) => setLoai(v as Loai)}>
          <SelectTrigger className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t('log.typeAll')}</SelectItem><SelectItem value="that">{t('log.typeReal')}</SelectItem><SelectItem value="thu">{t('log.typeTest')}</SelectItem></SelectContent>
        </Select>

        <Select value={thoiLuong} onValueChange={(v) => setThoiLuong(v as ThoiLuong)}>
          <SelectTrigger className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{(Object.keys(THOI_LUONG_LABEL) as ThoiLuong[]).map((tl) => <SelectItem key={tl} value={tl}>{THOI_LUONG_LABEL[tl]}</SelectItem>)}</SelectContent>
        </Select>

        <div className="flex items-center gap-1 rounded-md border px-2">
          <Input type="date" value={tu} onChange={(e) => setTu(e.target.value)} className="h-8 w-[8.5rem] border-0 px-1 text-xs shadow-none focus-visible:ring-0" />
          <span className="text-muted-foreground">–</span>
          <Input type="date" value={den} onChange={(e) => setDen(e.target.value)} className="h-8 w-[8.5rem] border-0 px-1 text-xs shadow-none focus-visible:ring-0" />
        </div>

        {dangLoc && <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setKetQua(TAT_CA_KQ); setThoiLuong('all'); setLoai('all') }}><X className="h-3.5 w-3.5" /> {t('log.clearFilter')}</Button>}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">{t('log.runCount', { count: hien.length })}</span>
          <Button variant="outline" size="sm" className="h-9" disabled={isFetching} onClick={() => refetch()}><RefreshCw className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} /> {t('common:actions.refresh')}</Button>
        </div>
      </div>

      {isLoading ? <Loading label={t('log.loading')} />
        : isError ? <ErrorState message={t('log.loadError')} />
        : hien.length === 0 ? (
          <EmptyState icon={History} title={t('log.emptyTitle')}
            description={runs.length === 0 ? t('log.emptyNone') : t('log.emptyFiltered')} />
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-muted/40 text-left">
                <th className="p-2">{t('log.start')}</th>{hienTenLuong && <th className="p-2">{t('log.flow')}</th>}
                <th className="p-2">{t('log.contact')}</th><th className="p-2">{t('log.steps')}</th><th className="p-2">{t('log.duration')}</th><th className="p-2">{t('log.result')}</th><th className="p-2" />
              </tr></thead>
              <tbody>
                {hien.map((r) => (
                  <tr key={r.runId} className="cursor-pointer border-t hover:bg-accent/30" onClick={() => setMo(r)}>
                    <td className="whitespace-nowrap p-2 tabular-nums text-muted-foreground"><Clock className="mr-1 inline h-3 w-3" />{fmt(r.startedAt)}</td>
                    {hienTenLuong && <td className="max-w-[14rem] truncate p-2 font-medium">{r.ruleName}</td>}
                    <td className="max-w-[12rem] truncate p-2">{r.khach ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="p-2 tabular-nums">{r.steps.filter((s) => s.status === 'ok').length}/{r.steps.length}</td>
                    {/* Lượt còn nằm chờ: tính tới hiện tại, chưa có điểm kết thúc. */}
                    <td className="whitespace-nowrap p-2 tabular-nums text-muted-foreground">{r.ketQua === 'dang_cho' ? `${moTaThoiLuong(Date.now() - new Date(r.startedAt).getTime())}…` : moTaThoiLuong(r.thoiLuongMs)}</td>
                    <td className="p-2"><span className="flex flex-wrap items-center gap-1"><Badge variant={KET_QUA_LABEL[r.ketQua].variant}>{KET_QUA_LABEL[r.ketQua].ten}</Badge>{r.isTest && <Badge variant="outline" className="text-[10px]">{t('log.testBadge')}</Badge>}</span></td>
                    <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setMo(r) }}>{t('common:actions.details')}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
            {isFetchingNextPage ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null} {t('log.loadMore')}
          </Button>
        </div>
      )}

      <Dialog open={!!mo} onOpenChange={(o) => { if (!o) setMo(null) }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">{mo?.ruleName}{mo && <Badge variant={KET_QUA_LABEL[mo.ketQua].variant}>{KET_QUA_LABEL[mo.ketQua].ten}</Badge>}</DialogTitle>
            <DialogDescription>{mo?.isTest ? t('log.runTest') : t('log.runReal')} · {fmt(mo?.startedAt)} · {moTaThoiLuong(mo?.thoiLuongMs ?? 0)}{mo && mo.soDoan > 1 ? ` · ${t('log.segments', { count: mo.soDoan })}` : ''}</DialogDescription>
          </DialogHeader>
          {mo && <ChiTietLanChay run={mo} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Hộp Nhật ký (tiêu đề như Lark: tên luồng | Nhật ký hoạt động). */
export function NhatKyDialog({ open, onClose, ruleId, tenLuong, kinds }: { open: boolean; onClose: () => void; ruleId?: string; tenLuong?: string; kinds?: Map<string, KindKhoi> }) {
  const { t } = useTranslation('autoConfig')
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">{tenLuong && <><span>{tenLuong}</span><span className="text-muted-foreground">|</span></>}<span className="font-normal">{t('log.dialogTitle')}</span></DialogTitle>
          <DialogDescription>{t('log.dialogDesc')}</DialogDescription>
        </DialogHeader>
        {open && <NhatKyHoatDong ruleId={ruleId} hienTenLuong={!ruleId} kinds={kinds} />}
      </DialogContent>
    </Dialog>
  )
}
